'use client';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import cx from 'classnames';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { CatIconShop } from 'blocks-nextjs/blocks-react/components/Icons/Shop';
import { CatIconEditBox } from 'blocks-nextjs/blocks-react/components/Icons/EditBox';
import { CatIconCancel } from 'blocks-nextjs/blocks-react/components/Icons/Cancel';
import { CatIconSwap } from 'blocks-nextjs/blocks-react/components/Icons/Swap';
import { useQuickToast } from 'hooks/use-toast';
import { deselectEquipment } from 'services/equipment/actions';
import { ROOT_PORTALS } from 'constants/targets';
import { Conditional, Portal } from 'components/pcc-components';
import { useMyEquipmentDrawer } from '../my-equipment-drawer-context';
import { SelectedEquipmentActionButton } from './selected-equipment-action-button';
import { SelectedEquipmentInfo } from 'app/[lang]/[esite]/(core)/_header/components/equipment-widget/types';
import ValidateAssetModal from '../my-equipment-modal/validate-asset-modal';
import styles from './selected-equipment-drawer.module.scss';
import { CatIconDashboard2 } from 'blocks-nextjs/blocks-react/components/Icons/Dashboard2';
import { navigateTo } from '@cat-ecom/common/utils/browserUtils';
import { useParams, usePathname } from 'next/navigation';
import { checkIsAlpPage, redirectToNewAlpUrl } from '../utils';
import { useRouter } from 'next/navigation';

export const SelectedEquipmentDrawerButtons = ({
  showChangeEquipment = false,
  selectedEquipmentInfo = {},
  selectedEquipmentShopPartsUrl,
  selectedEquipmentViewDashboardUrl,
  assetLandingFeatureFlag
}: {
  showChangeEquipment?: boolean;
  selectedEquipmentInfo: SelectedEquipmentInfo;
  selectedEquipmentShopPartsUrl: string;
  selectedEquipmentViewDashboardUrl: string;
  assetLandingFeatureFlag: boolean;
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer);
  // part-name should be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
  // part-number should be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
  const params = useParams();
  const { isAlpSubpage, isAlp } = checkIsAlpPage(params, pathname);

  const { model, productFamily } = selectedEquipmentInfo;
  const isChild = true;
  const {
    editEquipmentDrawer,
    selectedEquipmentDrawer,
    selectEquipmentDrawer
  } = useMyEquipmentDrawer();
  const toast = useQuickToast();

  const [isEditModalActive, setIsEditModalActive] = useState(false);
  const handleOpenDrawer = (drawer) => {
    if (
      drawer === editEquipmentDrawer &&
      selectedEquipmentInfo?.isValidatedAsset
    ) {
      setIsEditModalActive(true);
    } else {
      drawer.openDrawer(null, isChild);
    }
  };

  const currentDeselectText = t('MEQ_DESELECT_MESSAGE', {
    0: model,
    1: capitalizeText(productFamily)
  });

  const handleDeselectEquipment = async () => {
    const { status } = await deselectEquipment();
    if (status === 'success') {
      selectedEquipmentDrawer.closeDrawer();
      toast.success({
        message: currentDeselectText,
        delay: 5
      });
    }
  };

  const handleClick = (url) => {
    navigateTo(url);
  };

  const router = useRouter();
  return (
    <>
      <div className={cx(['my-3', styles['equipment-actions__container']])}>
        <Conditional test={!isAlp}>
          <SelectedEquipmentActionButton
            text="VIEW_DASHBOARD"
            onClick={() => {
              selectedEquipmentDrawer.closeDrawer();
              redirectToNewAlpUrl(
                router,
                assetLandingFeatureFlag,
                params,
                selectedEquipmentViewDashboardUrl
              );
            }}
          >
            <CatIconDashboard2 />
          </SelectedEquipmentActionButton>
        </Conditional>
        <SelectedEquipmentActionButton
          text="SHOP_PARTS"
          onClick={() => handleClick(selectedEquipmentShopPartsUrl)}
        >
          <CatIconShop />
        </SelectedEquipmentActionButton>
        <Conditional test={showChangeEquipment}>
          <SelectedEquipmentActionButton
            text="CHANGE_EQUIPMENT"
            onClick={() => handleOpenDrawer(selectEquipmentDrawer)}
          >
            <CatIconSwap />
          </SelectedEquipmentActionButton>
        </Conditional>
        <SelectedEquipmentActionButton
          text="MEQ_EDIT_EQUIP"
          onClick={() => handleOpenDrawer(editEquipmentDrawer)}
        >
          <CatIconEditBox />
        </SelectedEquipmentActionButton>
        <Conditional test={!isAlpSubpage}>
          <SelectedEquipmentActionButton
            text="CLEAR_SELECTION"
            onClick={handleDeselectEquipment}
          >
            <CatIconCancel />
          </SelectedEquipmentActionButton>
        </Conditional>
      </div>
      <Portal id={ROOT_PORTALS}>
        <ValidateAssetModal
          isActive={isEditModalActive}
          closeEditModal={() => setIsEditModalActive(false)}
        />
      </Portal>
    </>
  );
};
